(ns minorodata.dependency-graph.subs
  "Reactive view of the dependency-graph `pipeline-dag` entity: resolves readable
   group/source labels and enriches transform/extract nodes, reusing the pipeline
   editor's enrich helpers."
  (:require
   [clojure.string :as str]
   [minorodata.app.utils :refer [drop-ns-in-keys]]
   [minorodata.dependency-graph.events :refer [dag-id graph-extract-ids tags-by-id]]
   [minorodata.pipeline-dag.layout :refer [container?]]
   [minorodata.sandbox.subs :refer [build-lookups container-label enrich-node resolve-subtitle]]
   [re-frame.core :as re-frame]))


(re-frame/reg-sub
 :dependency-graph.subs/show-sources?
 (fn [db _]
   (get-in db [:dependency-graph :show-sources?] true)))

(re-frame/reg-sub
 :dependency-graph.subs/show-tables?
 (fn [db _]
   (get-in db [:dependency-graph :show-tables?] false)))

(re-frame/reg-sub
 :dependency-graph.subs/show-groups?
 (fn [db _]
   (get-in db [:dependency-graph :show-groups?] true)))

(re-frame/reg-sub
 :dependency-graph.subs/compact?
 (fn [db _]
   (get-in db [:dependency-graph :compact?] false)))

(re-frame/reg-sub
 :dependency-graph.subs/failures-collapsed?
 (fn [db _]
   (get-in db [:dependency-graph :failures-collapsed?] false)))

;; Auto-refresh interval in ms, or nil when off. Drives the refresh control's
;; active state + radio selection.
(re-frame/reg-sub
 :dependency-graph.subs/auto-refresh-ms
 (fn [db _]
   (get-in db [:dependency-graph :auto-refresh-ms])))

;; Active group/tag filter — `{:group-ids #{...} :tags #{...}}`. Both are
;; multi-select sets. Drives the layout-time node drop in `filter-entities`
;; (see `minorodata.dependency-graph.events`).
(re-frame/reg-sub
 :dependency-graph.subs/filters
 (fn [db _]
   (get-in db [:dependency-graph :filters] {:group-ids #{} :tags #{}})))

;; `transform-id -> [tags]` lookup, built from the transform entities loaded by
;; `:pipeline-editor.api/fetch-transform-groups` (same data the pipeline editor
;; uses). Keyed by BOTH `:id` and `:master-id` so a graph node's `:transform-id`
;; resolves regardless of which the backend stamped on the node. Blank tags are
;; dropped so they never pollute the tag dropdown.
(re-frame/reg-sub
 :dependency-graph.subs/transform-tags
 :<- [:pipeline-transform/pull-many]
 (fn [transforms _]
   (tags-by-id transforms)))

;; `transform-id -> {:name :master-id :group-id :tags}` lookup, built from the
;; transforms already loaded by `:dependency-graph.api/fetch-transforms` (the same
;; `pipeline-transform` entities the tag filter reads). The sidebar resolves its
;; failure / recent-run row names + menu ids from this map instead of fetching each
;; transform individually. Keyed by BOTH `:id` and `:master-id` so a status's
;; transform-id resolves regardless of which the backend stamped (as `transform-tags`).
(re-frame/reg-sub
 :dependency-graph.subs/transform-index
 :<- [:pipeline-transform/pull-many]
 (fn [transforms _]
   (reduce (fn [m {:pipeline-transform/keys [id master-id group-id name tags]}]
             (let [entry {:name name :master-id master-id :group-id group-id
                          :tags (vec (remove str/blank? tags))}]
               (cond-> m
                 (some? id)        (assoc id entry)
                 (some? master-id) (assoc master-id entry))))
           {}
           transforms)))

(re-frame/reg-sub
 :dependency-graph.subs/tabs
 (fn [db _]
   (get-in db [:dependency-graph :tabs])))

(re-frame/reg-sub
 :dependency-graph.subs/active-tab-id
 (fn [db _]
   (get-in db [:dependency-graph :active-tab-id])))

;; Ready means something to DRAW, not merely something fetched: the raw graph lands
;; first and ELK lays it out after, so gating on the raw graph alone showed an empty
;; canvas for the length of the layout. An EMPTY graph never reaches layout, hence
;; the second arm — otherwise it would spin forever. Statuses are deliberately not
;; part of this; they arrive later and the canvas is useful without them.
(re-frame/reg-sub
 :dependency-graph.subs/relayouting?
 (fn [db [_ tab-id]]
   (contains? (get-in db [:dependency-graph :layout-pending]) tab-id)))

(re-frame/reg-sub
 :dependency-graph.subs/layout-failed?
 (fn [db [_ tab-id]]
   (boolean (get-in db [:dependency-graph :layout-failed tab-id]))))

(re-frame/reg-sub
 :dependency-graph.subs/tab-ready?
 (fn [db [_ tab-id]]
   (let [raw (get-in db [:dependency-graph :raw tab-id])]
     (boolean (and (some? raw)
                   (or (get-in db [:dependency-graph :laid-out tab-id])
                       (empty? (:nodes raw))))))))

;; True while any of the three per-node status fetches is in flight. Drives the
;; canvas banner and the refresh button's spinner.
(re-frame/reg-sub
 :dependency-graph.subs/statuses-loading?
 (fn [_ _]
   [(re-frame/subscribe [:mold-http/loading? :connections.api/fetch-transform-statuses])
    (re-frame/subscribe [:mold-http/loading? :connections.api/fetch-extract-statuses-for-graph])
    ;; never dispatched when the analytics-models feature is off, so this stays nil
    (re-frame/subscribe [:mold-http/loading? :analytics-models.api/fetch-statuses])])
 (fn [in-flight _]
   (boolean (some identity in-flight))))

(re-frame/reg-sub
 :dependency-graph.subs/timeline
 (fn [db _]
   (get-in db [:dependency-graph :timeline])))

(re-frame/reg-sub
 :dependency-graph.subs/logs
 (fn [db _]
   (get-in db [:dependency-graph :logs])))

(re-frame/reg-sub
 :dependency-graph.subs/focus-node-id
 (fn [db _]
   (get-in db [:dependency-graph :focus-node-id])))

(re-frame/reg-sub
 :dependency-graph.subs/hovered-master-id
 (fn [db _]
   (get-in db [:dependency-graph :hovered-master-id])))

;; Deliberately a per-node boolean rather than the id itself: every mounted node
;; recomputes `=` on hover, but only the two whose answer changed re-render.
;; Subscribing to the raw id would re-render every node on the canvas instead.
(re-frame/reg-sub
 :dependency-graph.subs/node-hovered?
 (fn [_ _]
   (re-frame/subscribe [:dependency-graph.subs/hovered-master-id]))
 (fn [hovered [_ master-id]]
   (boolean (and master-id (= master-id hovered)))))

;; Per-node, like `node-hovered?`, so a click re-renders only the nodes whose answer changed.
(re-frame/reg-sub
 :dependency-graph.subs/node-focused?
 (fn [_ _]
   (re-frame/subscribe [:dependency-graph.subs/focus-node-id]))
 (fn [focused [_ node-id]]
   (boolean (and node-id (= node-id focused)))))

;; Bumped on every `focus-node` dispatch so re-focusing the same node re-fires the
;; `ui.dag/viewport-controller` effect.
(re-frame/reg-sub
 :dependency-graph.subs/focus-tick
 (fn [db _]
   (get-in db [:dependency-graph :focus-tick])))


;; Extract-ids on the default "Pipelines" graph (the general view). Read from the
;; raw `dag-id` graph so membership is stable across the show-sources/layout
;; toggles, and matches what `reveal-node` can resolve.
;; Signalled off `raw-graph` rather than computed from `db` directly: this scans the
;; whole graph, and a computation-only `reg-sub` is a reaction over app-db — it
;; would re-run on EVERY dispatch, not just when the graph changes.
(re-frame/reg-sub
 :dependency-graph.subs/graph-extract-ids
 :<- [:dependency-graph.subs/raw-graph]
 (fn [raw _]
   (graph-extract-ids raw)))

;; `extract-id -> display name`, read from the raw "Pipelines"-tab nodes (which
;; already carry each extract's name). The sidebar resolves extract row names from
;; here instead of fetching each extract individually. Uses the stable raw `dag-id`
;; graph so it doesn't shift with the show-sources/layout toggles.
(re-frame/reg-sub
 :dependency-graph.subs/extract-name-index
 :<- [:dependency-graph.subs/raw-graph]
 (fn [raw _]
   (into {}
         (comp (filter #(= "extract" (:type %)))
               (keep (fn [{:keys [name metadata]}]
                       (when-let [eid (:extract-id metadata)]
                         [eid name]))))
         (:nodes raw))))

;; Failing extracts limited to those shown in the general view — the sidebar's
;; failures list shouldn't surface extracts that aren't part of the graph (and so
;; can't be revealed). Memoized so the returned vector keeps a stable reference.
(re-frame/reg-sub
 :dependency-graph.subs/failing-graph-extracts
 (fn [_ _]
   [(re-frame/subscribe [:statuses.subs/failed :extract])
    (re-frame/subscribe [:dependency-graph.subs/graph-extract-ids])])
 (fn [[failures extract-ids] _]
   (filterv #(contains? extract-ids (:extract-id %)) failures)))




;; Group/tag filtering no longer dims nodes in place — the filter now drops
;; non-matching nodes and re-runs ELK (see `filter-entities` / `layout-graph` in
;; `minorodata.dependency-graph.events`), so the `dag` sub just renders whatever
;; the (already filtered) layout produced.

;; -----------------------------------------------------------------------------
;; Filter options — the selectable groups/tags for the toolbar. Derived from the
;; RAW (unfiltered) graph, NOT the laid-out `dag` sub: the layout is filtered, so
;; deriving options from it would make every unpicked option vanish after the
;; first selection.
;; -----------------------------------------------------------------------------

(re-frame/reg-sub
 :dependency-graph.subs/raw-graph
 (fn [db [_ tab-id]]
   (get-in db [:dependency-graph :raw (or tab-id dag-id)])))

;; `{:group-sections [{:heading :options [{:value :label}]}] :tags [tag …]}` for
;; the filter toolbar. Group options are split into Sources / Transforms / Data
;; models sections (sections/labels resolved exactly as the container boxes are);
;; blank-labelled and empty sections are dropped. `:value` is the group id the
;; layout-side filter matches against.
;;
;; Scoped to the given TAB's raw graph: a subpipeline tab only offers the groups /
;; sources / data models / tags present in its own graph, not the whole pipeline's.
;; Derived from the RAW (unfiltered) graph so options don't vanish as the filtered
;; graph shrinks.
(re-frame/reg-sub
 :dependency-graph.subs/filter-options
 (fn [[_ tab-id] _]
   [(re-frame/subscribe [:dependency-graph.subs/raw-graph tab-id])
    (re-frame/subscribe [:pipeline-group/pull-many])
    (re-frame/subscribe [:source/pull-many])
    (re-frame/subscribe [:analytics-model-registry/pull-many])
    (re-frame/subscribe [:analytics-model/pull-many])
    (re-frame/subscribe [:dependency-graph.subs/transform-tags])])
 (fn [[raw groups raw-sources registry models tags-by-id] _]
   (let [sources         (mapv drop-ns-in-keys raw-sources)
         ;; registry project-name -> display-name, and model instance -> registry,
         ;; mirroring `container-label` / `assign-model-groups` respectively.
         registry-names  (into {} (map (juxt :analytics-model-registry/project-name
                                             :analytics-model-registry/display-name)) registry)
         model->registry (into {} (map (juxt :analytics-model/model-id
                                             :analytics-model/registry-model)) models)
         lookups        (build-lookups {:groups groups :sources sources})
         ;; {:extract {value label} :transform {…} :model {…}} — a value->label map
         ;; per section, deduping repeated group ids as we scan the raw nodes.
         by-section (reduce
                     (fn [acc {:keys [type metadata]}]
                       (case type
                         "transform" (if-let [gid (:group-id metadata)]
                                       (assoc-in acc [:transform gid]
                                                 (resolve-subtitle {:type "transform" :group-id gid} lookups))
                                       acc)
                         "extract"   (if-let [sid (:source-id metadata)]
                                       (assoc-in acc [:extract sid]
                                                 (resolve-subtitle {:type "extract" :group-id (:source-id metadata)} lookups))
                                       acc)
                         "model"     (if-let [reg (get model->registry (:model-id metadata))]
                                       (assoc-in acc [:model reg] (get registry-names reg reg))
                                       acc)
                         acc))
                     {}
                     (:nodes raw))
         options-for (fn [section]
                       (->> (get by-section section)
                            (map (fn [[value label]] {:value value :label label}))
                            (remove #(str/blank? (:label %)))
                            (sort-by (comp str/lower-case str :label))
                            vec))]
     {:group-sections (vec (for [[section heading] [[:extract "Sources"]
                                                     [:transform "Transform groups"]
                                                     [:model "Data models"]]
                                 :let [options (options-for section)]
                                 :when (seq options)]
                             {:heading heading :options options}))
      ;; tags scoped to THIS tab's transforms (look each graph transform up in the
      ;; global tags map) so a subpipeline only offers its own tags, not every tag.
      :tags           (->> (:nodes raw)
                           (filter #(= "transform" (:type %)))
                           (mapcat #(get tags-by-id (get-in % [:metadata :transform-id])))
                           distinct
                           (sort-by str/lower-case)
                           vec)})))

;; -----------------------------------------------------------------------------
;; Per-node live status — split OUT of the `dag` sub so a status refresh no longer
;; re-emits (and re-`clj->js`-es + re-diffs) every node. Each transform/extract node
;; subscribes to its OWN entry; re-frame's value-equality means only nodes whose
;; status actually changed re-render. The `*-status-index` subs are memoized (`:<-`),
;; so a refresh rebuilds the lookup once — not once per node.
;; -----------------------------------------------------------------------------

(re-frame/reg-sub
 :dependency-graph.subs/transform-status-index
 :<- [:transform-status-list/prop :transform-status-list/statuses]
 (fn [statuses _]
   (into {} (map (juxt :transform-id identity)) statuses)))

;; `{:status :last-run}` for one transform, or nil when it has never run. `:last-run`
;; is the whole status entry (carries started/completed-at for the execution
;; duration) — mirrors what `enrich-node` stamped onto `:data`.
(re-frame/reg-sub
 :dependency-graph.subs/transform-status
 :<- [:dependency-graph.subs/transform-status-index]
 (fn [index [_ transform-id]]
   (when-let [entry (get index transform-id)]
     {:status (:status entry) :last-run entry})))

(re-frame/reg-sub
 :dependency-graph.subs/dag
 (fn [[_ tab-id] _]
   [(re-frame/subscribe [:pipeline-dag/pull [:pipeline-dag/id (or tab-id dag-id)]])
    (re-frame/subscribe [:pipeline-group/pull-many])
    (re-frame/subscribe [:source/pull-many])
    (re-frame/subscribe [:analytics-model-registry/pull-many])])
 (fn [[dag groups raw-sources registry] [_ tab-id]]
   (when (not-empty dag)
     (let [sources (mapv drop-ns-in-keys raw-sources)
           ;; project name -> display name, for labelling model-group boxes.
           registry-names (into {}
                                (map (juxt :analytics-model-registry/project-name
                                           :analytics-model-registry/display-name))
                                registry)
           ;; focus and live status are read per node, so neither re-emits the graph
           lookups (build-lookups {:groups          groups
                                   :sources         sources
                                   ;; so a model node's subtitle resolves to the
                                   ;; registry model's display name rather than
                                   ;; falling back to the raw model-type
                                   :registry-models registry})
           ctx     {:node-id    nil
                    :preview-id nil
                    :sandbox?   false
                    :lookups    lookups}
           base    (mapv (fn [node]
                           (if (container? node)
                             (container-label node lookups registry-names)
                             (enrich-node ctx node)))
                         (:pipeline-dag/nodes dag))]
       {:id      (or tab-id dag-id)
        :nodes   base
        :edges   (:pipeline-dag/edges dag)}))))
